import React from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Clock, Phone, Navigation } from 'lucide-react';
import { NeonButton } from './ui/NeonButton';
// Fix default marker icons (CDN assets)
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Mall location — owner can edit address/phone text. Coordinates are live.
const ADDRESS = {
  name: 'Galaxy Mall — Level 2, Unit 214',
  street: '1200 Starlight Blvd',
  city: 'Los Angeles, CA 90015',
  lat: 33.3909168,
  lng: -111.6893387,
  phone: '(213) 555-0199'
};
const directionsUrl = 'https://www.google.com/maps/dir//33.3909168,-111.6893387';
const hours = [{
  day: 'Mon – Thu',
  time: '11:00 AM – 9:00 PM'
}, {
  day: 'Friday',
  time: '11:00 AM – 11:00 PM'
}, {
  day: 'Saturday',
  time: '10:00 AM – 11:00 PM'
}, {
  day: 'Sunday',
  time: '10:00 AM – 8:00 PM'
}];
export function LocationHours() {
  return <section id="location" className="relative w-full py-20 sm:py-28" data-id="element-51">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-52">
        <div className="mb-12 text-center" data-id="element-53">
          <h2 className="font-arcade text-4xl sm:text-5xl" data-id="element-54">
            <span className="neon-cyan" data-id="element-55">FIND</span>{' '}
            <span className="neon-pink" data-id="element-56">US</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-display text-purple-100/80" data-id="element-57">
            Come play in person — we're easy to find inside the mall.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-id="element-58">
          {/* Info card */}
          <motion.div initial={{
          opacity: 0,
          x: -24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} className="flex flex-col gap-6 rounded-2xl border-2 border-purple-500/40 bg-white/[0.03] p-7 backdrop-blur-sm shadow-[0_0_24px_rgba(168,85,247,0.3)]" data-id="element-59">
            <div className="flex items-start gap-4" data-id="element-60">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-pink-400 text-pink-200 shadow-[0_0_16px_rgba(236,72,153,0.6)]" data-id="element-61">
                <MapPin className="h-5 w-5" aria-hidden="true" data-id="element-62" />
              </span>
              <div data-id="element-63">
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white" data-id="element-64">
                  Location
                </h3>
                <p className="mt-1 font-display text-purple-100/85" data-id="element-65">
                  {ADDRESS.name}
                </p>
                <p className="font-display text-purple-100/70" data-id="element-66">
                  {ADDRESS.street}
                </p>
                <p className="font-display text-purple-100/70" data-id="element-67">
                  {ADDRESS.city}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-68">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-cyan-300 text-cyan-200 shadow-[0_0_16px_rgba(34,211,238,0.6)]" data-id="element-69">
                <Clock className="h-5 w-5" aria-hidden="true" data-id="element-70" />
              </span>
              <div className="w-full" data-id="element-71">
                <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white" data-id="element-72">
                  Hours
                </h3>
                <dl className="mt-2 space-y-1.5" data-id="element-73">
                  {hours.map(h => <div key={h.day} className="flex justify-between gap-4 font-display text-sm" data-id="element-74">
                      <dt className="text-purple-100/70" data-id="element-75">{h.day}</dt>
                      <dd className="text-purple-100/90" data-id="element-76">{h.time}</dd>
                    </div>)}
                </dl>
              </div>
            </div>

            <div className="flex items-center gap-4" data-id="element-77">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-purple-400 text-purple-200 shadow-[0_0_16px_rgba(168,85,247,0.6)]" data-id="element-78">
                <Phone className="h-5 w-5" aria-hidden="true" data-id="element-79" />
              </span>
              <a href={`tel:${ADDRESS.phone.replace(/[^0-9]/g, '')}`} className="font-display text-purple-100/85 transition-colors hover:text-cyan-200" data-id="element-80">
                {ADDRESS.phone}
              </a>
            </div>

            <NeonButton href={directionsUrl} gated color="purple" pulse className="mt-2 w-full" data-id="element-81">
              <Navigation className="h-5 w-5" aria-hidden="true" data-id="element-82" />
              Get Directions
            </NeonButton>
          </motion.div>

          {/* Map */}
          <motion.div initial={{
          opacity: 0,
          x: 24
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} className="min-h-[360px] overflow-hidden rounded-2xl border-2 border-cyan-400/50 shadow-[0_0_28px_rgba(34,211,238,0.4)]" data-id="element-83">
            <MapContainer center={[ADDRESS.lat, ADDRESS.lng]} zoom={15} scrollWheelZoom={false} style={{
            height: '100%',
            width: '100%',
            minHeight: 360
          }} data-id="element-84">
              <TileLayer attribution="&copy; OpenStreetMap contributors &copy; CARTO" url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-85" />
              <Marker position={[ADDRESS.lat, ADDRESS.lng]} icon={markerIcon} data-id="element-86">
                <Popup data-id="element-87">
                  <strong data-id="element-88">Claw Planet Arcade</strong>
                  <br data-id="element-89" />
                  {ADDRESS.name}
                </Popup>
              </Marker>
            </MapContainer>
          </motion.div>
        </div>
      </div>
    </section>;
}